// src/components/_templates/Kebab/index.js
import React, { useEffect } from "react";
import "./Kebab.scss";
import CloseIcon from "@material-ui/icons/Close";
import { HashLink as Link } from "react-router-hash-link";
import { Socmed } from "components/_templates";
import { useStateSelector, useActionDispatch } from "../Menu/hooks";
import { animated, useSpring, config } from "react-spring";
import { useLocation } from "react-router-dom";

const Kebab = () => {
  const { kebab } = useStateSelector();
  const { handle_kebab } = useActionDispatch();
  const location = useLocation();

  const closeAfter = (ms = 120) => {
    requestAnimationFrame(() => setTimeout(() => handle_kebab(false), ms));
  };

  const styles = useSpring({
    x: kebab ? 0 : 100,
    opacity: kebab ? 1 : 0,
    pointerEvents: kebab ? "all" : "none",
    config: config.gentle,
  });

  // header-aware smooth scroll (kept for in-page jumps)
  const scrollWithOffset = (el) => {
    const headerH = document.querySelector(".menu-bar")?.offsetHeight || 0;
    const extra = 20;
    const y = el.getBoundingClientRect().top + window.pageYOffset;
    const top = Math.max(0, y - headerH - extra);
    window.scrollTo({ top, behavior: "smooth" });
  };

  // Always point to root + hash for these sections
  const links = [
    { to: "/#our-services", label: "Our Services" },
    { to: "/#reviews", label: "Reviews" },
    { to: "/#featured", label: "Featured Project" },
    { to: "/#subscribe", label: "Subscribe" },
    { to: "/#about", label: "About" },
    { to: "/contact-us", label: "Contact us" },
  ];

  // If not on home, force a real nav so the target exists, then close the kebab
  const handleHashClick = (to) => (e) => {
    if (to.startsWith("/#") && location.pathname !== "/") {
      e.preventDefault();
      window.location.assign(to); // hard navigate to /#section (browser will scroll)
      closeAfter(0);
      return;
    }
    // On home already → let HashLink do its thing
    closeAfter();
  };

  // ESC to close while open
  useEffect(() => {
    if (!kebab) return;
    const onKey = (e) => { if (e.key === "Escape") closeAfter(0); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [kebab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOverlayClick = () => closeAfter(0);

  return (
    <animated.div
      className="kebab-wrap"
      style={styles}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Site menu"
    >
      <div
        className="kebab-bg-body"
        onClick={(e) => e.stopPropagation()} // clicks inside panel don't close
      >
        <div className="inner-wrap">
          <div className="link-wrap">
            {/* Top: BrandKeeper (shiny red) */}
            <Link
              to="/brandkeeper"
              className="brandkeeper-link"
              onClick={() => closeAfter()}
              aria-label="BrandKeeper landing page"
            >
              <span className="bk-text">
                BrandKeeper<span aria-hidden="true">™</span>
              </span>
              <span className="shine" aria-hidden="true" />
            </Link>

            {/* Existing menu items */}
            {links.map((link, id) =>
              link.to.startsWith("/#") ? (
                <Link
                  key={id}
                  to={link.to}
                  smooth
                  scroll={scrollWithOffset}
                  onClick={handleHashClick(link.to)}
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={id}
                  to={link.to}
                  smooth
                  onClick={() => closeAfter()}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          <div className="kebab-socialmedia-wrap">
            <Socmed />
          </div>

          <div className="kebab-copy">
            <h4>
              © Martley Strategy™. All Rights Reserved. The Martley Strategy™
              name and logo are trademarks of Martley Strategy. Unauthorized
              use, reproduction, or distribution is strictly prohibited.
            </h4>
          </div>
        </div>

        <div className="kebab-close" onClick={() => closeAfter()}>
          <CloseIcon fontSize="large" />
        </div>
      </div>
    </animated.div>
  );
};

export default Kebab;
